import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import theme from '../../theme';
import { JobCard } from '../../types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface WorkTypeLineChartProps {
  jobCards: JobCard[];
  workType: 'maintenance' | 'accident' | 'alQouz' | 'agency';
  title: string;
  color: string;
  selectedMonth: number; // 0-indexed: 7=August, 8=September, 9=October
}

export const WorkTypeLineChart: React.FC<WorkTypeLineChartProps> = ({
  jobCards,
  workType,
  title,
  color,
  selectedMonth,
}) => {
  const isWeb = Platform.OS === 'web';

  const chartData = useMemo(() => {
    // Filter job cards by work type
    const filteredCards = jobCards.filter(jc => {
      switch (workType) {
        case 'maintenance':
          return jc.underMaintenance;
        case 'accident':
          return jc.accidentRepair;
        case 'alQouz':
          return jc.underMaintenanceAlQouz;
        case 'agency':
          return jc.underMaintenanceAgency;
        default:
          return false;
      }
    });

    // Handle "Total" view (selectedMonth === -1)
    if (selectedMonth === -1) {
      const now = new Date();
      const currentMonth = now.getMonth();
      const year = now.getFullYear();
      
      // Combine data from last 4 months (current + 3 before)
      const labels: string[] = [];
      const counts: number[] = [];
      
      for (let monthOffset = 3; monthOffset >= 0; monthOffset--) {
        const targetMonth = currentMonth - monthOffset < 0 ? currentMonth - monthOffset + 12 : currentMonth - monthOffset;
        const targetYear = currentMonth - monthOffset < 0 ? year - 1 : year;
        const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
        const isCurrentMonth = targetMonth === currentMonth && targetYear === year;
        const maxDay = isCurrentMonth ? now.getDate() : daysInMonth;
        
        for (let day = 1; day <= maxDay; day++) {
          const date = new Date(targetYear, targetMonth, day);
          date.setHours(0, 0, 0, 0);
          
          // Show label every 7 days or first day of month
          const label = (day === 1 || day % 7 === 1) ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
          labels.push(label);
          
          const count = filteredCards.filter(jc => {
            const handoverDate = jc.handoverDate ? new Date(jc.handoverDate) : null;
            const returnDate = jc.returnDate ? new Date(jc.returnDate) : new Date();
            
            if (!handoverDate) return false;
            
            handoverDate.setHours(0, 0, 0, 0);
            returnDate.setHours(23, 59, 59, 999);
            
            return date >= handoverDate && date <= returnDate;
          }).length;
          
          counts.push(count);
        }
      }
      
      const average = counts.length > 0 ? counts.reduce((a, b) => a + b, 0) / counts.length : 0;
      const peak = counts.length > 0 ? Math.max(...counts) : 0;
      const current = counts[counts.length - 1] || 0;
      
      return { labels, counts, average, peak, current };
    }

    // Get days in the selected month
    const now = new Date();
    const year = now.getFullYear();
    const monthIndex = selectedMonth; // 0-indexed
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    
    // For current month, only calculate up to today
    const isCurrentMonth = monthIndex === now.getMonth() && year === now.getFullYear();
    const maxDay = isCurrentMonth ? now.getDate() : daysInMonth;
    
    const labels: string[] = [];
    const counts: number[] = [];

    for (let day = 1; day <= maxDay; day++) {
      const date = new Date(year, monthIndex, day);
      date.setHours(0, 0, 0, 0);
      
      // Format label (show date for every 5 days)
      const label = day % 5 === 1 || day === 1 ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
      labels.push(label);

      // Count vehicles in workshop on this date
      const count = filteredCards.filter(jc => {
        const handoverDate = jc.handoverDate ? new Date(jc.handoverDate) : null;
        const returnDate = jc.returnDate ? new Date(jc.returnDate) : new Date();
        
        if (!handoverDate) return false;
        
        handoverDate.setHours(0, 0, 0, 0);
        returnDate.setHours(23, 59, 59, 999);
        
        return date >= handoverDate && date <= returnDate;
      }).length;

      counts.push(count);
    }

    const average = counts.length > 0 ? counts.reduce((a, b) => a + b, 0) / counts.length : 0;
    const peak = counts.length > 0 ? Math.max(...counts) : 0;
    const current = counts[counts.length - 1] || 0;

    return { labels, counts, average, peak, current };
  }, [jobCards, workType, selectedMonth]);

  if (!isWeb) {
    return (
      <View style={styles.fallback}>
        <Text style={styles.fallbackText}>Chart available on web only</Text>
      </View>
    );
  }

  const data = {
    labels: chartData.labels,
    datasets: [
      {
        label: title,
        data: chartData.counts,
        borderColor: color,
        backgroundColor: `${color}33`,
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            return `${context.parsed.y} vehicles`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>{title}</Text>
      </View>
      <View style={styles.statsRow}>
        <View style={styles.statItem}>
          <Text style={styles.statLabel}>Current</Text>
          <Text style={[styles.statValue, { color }]}>{chartData.current}</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statLabel}>Average</Text>
          <Text style={[styles.statValue, { color }]}>{chartData.average.toFixed(1)}</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statLabel}>Peak</Text>
          <Text style={[styles.statValue, { color }]}>{chartData.peak}</Text>
        </View>
      </View>
      <View style={styles.chartContainer}>
        <Line data={data} options={options} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  header: {
    marginBottom: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.background,
    borderRadius: 8,
  },
  statItem: {
    alignItems: 'center',
  },
  statLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginBottom: 4,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  chartContainer: {
    height: 200,
  },
  fallback: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
    height: 200,
    marginBottom: 12,
  },
  fallbackText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
});
